import { z } from 'zod';

export enum FeatureType {
  Boolean = 1,
  String = 2,
  Number = 3,
  Markdown = 4,
}

const BooleanFeature = z.object({
  type: z.literal(FeatureType.Boolean),
  value: z.boolean(),
});
export type BooleanFeature = z.infer<typeof BooleanFeature>;

const StringFeature = z.object({
  type: z.literal(FeatureType.String),
  value: z.string(),
});
export type StringFeature = z.infer<typeof StringFeature>;

const NumberFeature = z.object({
  type: z.literal(FeatureType.Number),
  value: z.number(),
});
export type NumberFeature = z.infer<typeof NumberFeature>;

const MarkdownFeature = z.object({
  type: z.literal(FeatureType.Markdown),
  value: z.string(),
});
export type MarkdownFeature = z.infer<typeof MarkdownFeature>;

export const FeatureValueSchema = z.discriminatedUnion('type', [
  BooleanFeature,
  StringFeature,
  NumberFeature,
  MarkdownFeature,
]);
export type FeatureValue = z.infer<typeof FeatureValueSchema>;

const BlankValidation = <FT extends FeatureType>(ft: FT) => {
  return z.object({
    type: z.literal(ft),
  });
};

const BooleanValidation = BlankValidation(FeatureType.Boolean);
export type BooleanValidation = z.infer<typeof BooleanValidation>;

const StringValidation = z.object({
  type: z.literal(FeatureType.String),
  validValues: z.array(z.string()).optional(),
});
export type StringValidation = z.infer<typeof StringValidation>;

const NumberValidation = BlankValidation(FeatureType.Number);
export type NumberValidation = z.infer<typeof NumberValidation>;

const MarkdownValidation = BlankValidation(FeatureType.Markdown);
export type MarkdownValidation = z.infer<typeof MarkdownValidation>;

export const FeatureValidationSchema = z.discriminatedUnion('type', [
  BooleanValidation,
  StringValidation,
  NumberValidation,
  MarkdownValidation,
]);
export type FeatureValidation = z.infer<typeof FeatureValidationSchema>;

export const FeatureValidationSchemaMap = {
  [FeatureType.Boolean]: BooleanValidation,
  [FeatureType.String]: StringValidation,
  [FeatureType.Number]: NumberValidation,
  [FeatureType.Markdown]: MarkdownValidation,
};

export const FeatureDetailSchema = z
  .object({
    propName: z.string(),
    displayName: z.string(),
    description: z.string(),
    category: z.object({
      id: z.number(),
      name: z.string(),
    }),
    source: z
      .object({
        name: z.string(),
      })
      .nullable(),
    feature: FeatureValueSchema,
    validation: FeatureValidationSchema,
  })
  .refine((a) => a.feature.type === a.validation.type, {
    error: 'Feature type and validation type do not match',
  });
export type FeatureDetail = z.infer<typeof FeatureDetailSchema>;

export const homePageSchema = z.enum(['chat', 'workspace', 'analyst-main']);
export type HomePage = z.infer<typeof homePageSchema>;

//TODO: update this schema to define these fields but also allow any other fields
export const flatFeaturesModel = z.object({
  autoTagFiles: z.boolean(),
  workspaces: z.boolean(),
  workspaceBrands: z.boolean(),
  hcpProfiles: z.boolean(),
  chatVoiceInput: z.boolean(),
  chatReadAloud: z.boolean(),
  chatVoiceSource: z.union([z.enum(['polly']), z.string()]),
  chatWelcomeScreen: z.boolean(),
  smartRouting: z.boolean(),
  voiceMode: z.boolean(),
  deleteChatsOnSessionEnd: z.boolean(),
  chatAggregationVisualization: z.boolean(),
  hcpSchedulingAssistant: z.boolean(),
  followUps: z.boolean(),
  // hcpLogInteraction: z.boolean(),
  predictiveScheduling: z.boolean(),
  chatGuidelines: z.string().nullable(),
  chatExpirationHours: z.number().nullable(),
  speechServicesProvider: z.enum(['browser', 'azure-speech-services', 'aws-speech-services', 'custom']),
  uploadFiles: z.boolean(),
  copyChatMessages: z.boolean(),
  shareChats: z.boolean(),
  exportChatData: z.boolean(),
  engagementResponseLabels: z.boolean(),
  engagementFilter: z.boolean(),
  keyDiscussionQuestionsDisclaimer: z.string().nullable(),
  chatIconVisibility: z.boolean(),
  supportIcon: z.string(),
  documentIconVisibility: z.boolean(),
  chatAuraIframesEnabled: z.boolean().optional(),
  chatFileUploads: z.boolean(),

  hcpContactInformation: z.boolean(),
  hcpInteractionsSummary: z.boolean(),
  hcpKeyDiscussionQuestions: z.boolean(),
  hcpKeyInsights: z.boolean(),
  hcpLeaderTopics: z.boolean(),
  hcpClinicalTrials: z.boolean(),
  hcpPublications: z.boolean(),
  hcpConferencePresence: z.boolean(),
  browserTabName: z.string().nullable(),
  complianceLayer: z.boolean(),
  maxUserChatQueries: z.number().nullable().prefault(5000),
  disableChatInputWithLandingCards: z.boolean(),

  homeScreen: homePageSchema.nullable().prefault('chat'),
  homeButton: z.boolean(),
  homeButtonTopHeader: z.boolean(),
  chatContextSelector: z.boolean(),
  applicationTooltip: z.string().nullable(),
});
export type FlatFeatures = z.infer<typeof flatFeaturesModel>;

const ParserMap = {
  [FeatureType.Boolean]: z.enum(['true', 'false']).transform((value) => value === 'true'),
  [FeatureType.String]: z.string(),
  [FeatureType.Number]: z.coerce.number(),
  [FeatureType.Markdown]: z.string(),
};

type ParseResult =
  | {
      success: false;
      message: string;
    }
  | {
      success: true;
      value: FeatureValue;
    };
export function parseFeatureValue(value: string, type: FeatureType): ParseResult {
  const parser = ParserMap[type];

  if (!parser) {
    return {
      success: false as const,
      message: `No parser found for feature type ${type}`,
    };
  }

  const result = parser.safeParse(value);
  if (!result.success) {
    return {
      success: false as const,
      message: result.error.message,
    };
  } else {
    return {
      success: true,
      value: {
        value: parser.parse(value),
        type,
      } as FeatureValue,
    };
  }
}

type ValidationParseResult =
  | {
      success: false;
      message: string;
    }
  | {
      success: true;
      value: FeatureValidation;
    };

export function parseFeatureValidations(value: object, type: FeatureType): ValidationParseResult {
  const parser = FeatureValidationSchemaMap[type];

  const result = parser.safeParse({ ...value, type });
  if (!result.success) {
    return {
      success: false as const,
      message: result.error.message,
    };
  } else {
    return {
      success: true,
      value: result.data as FeatureValidation,
    };
  }
}

export function stringifyFeatureValue(featureValue: FeatureValue): string {
  switch (featureValue.type) {
    case FeatureType.Boolean:
      return featureValue.value ? 'true' : 'false';
    case FeatureType.String:
      return featureValue.value;
    case FeatureType.Number:
      return featureValue.value.toString();
    case FeatureType.Markdown:
      return featureValue.value;
    default:
      throw new Error(`Unknown feature type: ${(featureValue as FeatureValue).type}`);
  }
}
